import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AxiosInstance from '../../AxiosInstance';
import config from '../../config/frontend.config';
import NewBuilderJS from './NewBuilderJS';
import './NewBuilderJS.css';

const NewBuilderJSPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState('/themes/standard/Y0_Blank');

  // Available themes
  const themes = [
    { name: 'Blank', path: '/themes/standard/Y0_Blank' },
    { name: '1 Column Layout', path: '/themes/standard/Y1_a_1_Column_Layout' },
    { name: '1-2 Column Layout', path: '/themes/standard/Y1_b_1-2-Column_Layout' },
    { name: '1-2-1 Column Layout', path: '/themes/standard/Y1_c_1-2-1_Column_Layout_theme' },
    { name: '1-3 Column Layout', path: '/themes/standard/Y1_d_1-3_Column_Layout_theme' },
    { name: '1-3-1 Column Layout', path: '/themes/standard/Y1_e_1-3-1_Column_Layout_theme' },
    { name: '1-3-2 Column Layout', path: '/themes/standard/Y1_f_1-3-2_Column_Layout_theme' },
    { name: '2 Column Layout', path: '/themes/standard/Y2_a_2_Column_Layout_theme' },
    { name: '2-1 Column Layout', path: '/themes/standard/Y2_b_2-1_Column_Layout_theme' },
    { name: '2-1-2 Column Layout', path: '/themes/standard/Y2_c_2-1-2_Column_Layout_theme' },
    { name: '3 Column Layout', path: '/themes/standard/Y3_a_3_Column_Layout_theme' },
    { name: '3-1-3 Column Layout', path: '/themes/standard/Y3_b_3-1-3_Column_Layout_theme' },
  ];

  // Load existing page if ID is provided
  useEffect(() => {
    if (id) {
      loadPage(id);
    }
  }, [id]);

  const loadPage = async (pageId) => {
    try {
      setIsLoading(true);
      
      if (!pageId) {
        toast.error('Template ID is missing');
        return;
      }
      
      // Use template API endpoint with AxiosInstance
      const response = await AxiosInstance.get(`/templates/by-template-id/${pageId}`);
      
      // Check if response and response.data exist
      if (!response) {
        throw new Error('No response received from server');
      }
      
      if (!response.data) {
        throw new Error('Response data is null or undefined');
      }
      
      // Check response structure
      if (response.data.statusCode === 200 && response.data.data) {
        // Extract the template data
        const templateData = response.data.data;
        
        // Set page data - BuilderJS expects the JSON data
        if (templateData.json) {
          setPageData(templateData.json);
          toast.success('Template loaded successfully');
        } else {
          console.warn('Template loaded but no JSON data found');
          toast.warning('Template loaded but has no content');
          setPageData(null);
        }
      } else if (response.data.statusCode === 404) {
        toast.error('Template not found');
        setPageData(null);
      } else {
        const errorMsg = response.data?.message || 'Failed to load template';
        toast.error('Failed to load template: ' + errorMsg);
        console.error('Unexpected response structure:', response.data);
      }
    } catch (error) {
      console.error('Error loading page:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        data: error.response?.data
      });
      
      // Better error handling
      let errorMessage = 'Failed to load template';
      if (error.response) {
        // Server responded with error status
        if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data?.statusCode) {
          errorMessage = `Server error: ${error.response.data.statusCode} - ${error.response.data.message || 'Unknown error'}`;
        } else if (typeof error.response.data === 'string' && error.response.data.includes('<!DOCTYPE')) {
          errorMessage = 'Server returned HTML instead of JSON. Template endpoint may not exist.';
        } else {
          errorMessage = `Server error: ${error.response.status}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error('Error loading template: ' + errorMessage);
      setPageData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (jsonData, htmlData) => {
    try {
      setIsLoading(true);
      
      const userId = config.getUserId();
      if (!userId) {
        toast.error('User not authenticated. Please login again.');
        return;
      }

      // Ensure jsonData is an object
      let jsonObject = jsonData;
      if (typeof jsonData === 'string') {
        try {
          jsonObject = JSON.parse(jsonData);
        } catch (e) {
          console.warn('JSON data is not valid JSON string, using as object');
          jsonObject = jsonData;
        }
      }

      // Validate jsonObject is actually an object
      if (typeof jsonObject !== 'object' || jsonObject === null) {
        toast.error('Invalid template data. Please try again.');
        console.error('Invalid jsonData:', jsonData);
        return;
      }

      const requestData = {
        userId,
        templateName: `New BuilderJS Template ${new Date().toLocaleString()}`,
        html: htmlData || '',
        json: jsonObject, // API expects object, not string - CompanyId will be set automatically by API
      };

      let response;
      if (id) {
        // Update existing template (CompanyId is preserved automatically)
        response = await AxiosInstance.put(`/templates/${id}`, requestData);
      } else {
        // Create new template (CompanyId will be set from user's CompanyId)
        response = await AxiosInstance.post('/templates', requestData);
      }

      if (response.data?.statusCode === 200 || response.data?.statusCode === 201) {
        toast.success('Template saved successfully');
        
        // If it's a new template, update URL without navigating to avoid reload
        // This prevents the loadPage error since we already have the data
        if (!id && response.data?.data?.TemplateId) {
          // Update URL without triggering navigation/reload
          window.history.replaceState({}, '', `/new-builderjs/${response.data.data.TemplateId}`);
          // Don't call navigate() as it will trigger useEffect and loadPage
        }
      } else {
        throw new Error(response.data?.message || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save template';
      toast.error('Error saving template: ' + errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = (htmlData) => {
    // Create a blob and download
    const blob = new Blob([htmlData], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `page-${Date.now()}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Page exported successfully');
  };

  const handleThemeChange = (themePath) => {
    setSelectedTheme(themePath);
    toast.info('Theme changed. Please reload the page to apply the new theme.');
  };

  if (isLoading && id && !pageData) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading page...</div>
      </div>
    );
  }

  return (
    <div className="new-builderjs-page">
      {/* Builder Component */}
      <NewBuilderJS
        initialData={pageData}
        onSave={handleSave}
        onExport={handleExport}
        themeUrl={selectedTheme}
      />
    </div>
  );
};

export default NewBuilderJSPage;


